import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { ErgonomicsEvaluationReportDialogComponent } from 'app/assessment/evaluation/components/ergonomics-evaluation-report-dialog/ergonomics-evaluation-report-dialog.component';
import { EntityTypeDynamic } from 'app/assessment/models';
import { SurveyReportDialogComponent } from 'app/assessment/survey/components/survey-report-dialog/survey-report-dialog.component';
import { AppScopes } from 'app/core/authentication/models/scopes.enum';
import { AuthService } from 'app/core/authentication/services';
import { MceSettings } from 'app/core/mce-config/mce-editor-config.constant';
import { User, UserInterface } from 'app/core/user/models';
import utilityCommon from 'app/core/utility/utility-common';
import { GlobalConstants } from 'app/shared/constants/globalconstants';
import { getBackupLanguageValue } from 'app/shared/utilities/backup-language.utitlity';
import { environment } from 'environments/environment.prod';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { RecommendationInterface } from '../../models';
import { RecommendationService } from '../../services/recommendation.service';

@Component({
  selector: 'cz-recommendation-card-dialog',
  templateUrl: './recommendation-card-dialog.component.html',
  styleUrls: ['./recommendation-card-dialog.component.scss'],
})
export class RecommendationCardDialogComponent implements OnInit, OnDestroy {

  public isReadOnly: boolean = true;
  public canClose: boolean = false;
  public canDelete: boolean = false;
  public isLinked: boolean = false;
  public recommendationFormGroup: FormGroup;
  public priorities: { value: number, viewValue: string }[];
  public loggedInUser: UserInterface;
  public browser: string = 'unknown';

  public tinyApiKey: string = environment.tinyMceKey;
  public descriptionDisplayConfig: any = MceSettings.readonlyDisplay;
  public descriptionControl: FormControl = new FormControl('');
  public detailsControl: FormControl = new FormControl('');
  public readonly recommendationsCompletedForUser$: BehaviorSubject<Readonly<RecommendationInterface[]>>;
  public recommendation: RecommendationInterface;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public dialog: MatDialog,
    private readonly _dialogRef: MatDialogRef<RecommendationCardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _formBuilder: FormBuilder,
    private _httpClient: HttpClient,
    private readonly _authService: AuthService,
    private readonly _recommendationService: RecommendationService,
    private _snackBar: MatSnackBar,
    private _translateService: TranslateService,
    private readonly _GlobalConstants: GlobalConstants,
  ) {
    // Get Browser name
    this.browser = utilityCommon.getBrowserName();
    this.recommendationsCompletedForUser$ = new BehaviorSubject<Readonly<RecommendationInterface[]>>(undefined);
  }

  public ngOnInit(): void {
    if (!this.data.recommendation) {
      throw new Error('No Recommendation provided.');
    }
    // TODO: translate priority display values
    this.priorities = [
      { value: 1, viewValue: '$admin_recomm_config_item_priority_high' },
      { value: 2, viewValue: '$admin_recomm_config_item_priority_medium' },
      { value: 3, viewValue: '$admin_recomm_config_item_priority_low' },
    ];

    this.recommendation = this.data.recommendation;

    this.descriptionControl.patchValue(this.getBackupValue('text', this.recommendation, true));
    this.detailsControl.patchValue(this.recommendation.detailsAsHtml);

    this.isLinked = this.recommendation.relatedAssessmentId > 0;

    // Set form permissions for the viewing user
    this._authService.getLoggedInUser()
      .pipe(take(1))
      .subscribe((user: UserInterface) => {
        // Make read only for subject and anyone not the creator of the recommendation if the recommendation is still open
        if (!this.recommendation.isComplete) {
          this.isReadOnly = (user.id === this.recommendation.subject.id || user.id !== this.recommendation.createdBy.id);
        }

        this._authService.isLoggedInUserAuthorized([AppScopes.CASE_MANAGEMENT_MASTER])
          .pipe(
            take(1))
          .subscribe((isRecommendationAdmin: boolean) => {
            this.canClose = user.id === this.recommendation.subject.id && !this.recommendation.isComplete;

            if (!this.canClose && isRecommendationAdmin) {
              this.canClose = isRecommendationAdmin && !this.recommendation.isComplete;
            }
          });

        this.loggedInUser = new User(user);
      });

    if (!this.isReadOnly) {
      this.recommendationFormGroup = this._formBuilder.group({
        priorityCtrl: [{ value: this.recommendation.priority, disabled: this.isReadOnly }],
      });
    }
  }

  public ngOnDestroy(): void {
  }

  public openPdfDialog(): void {
    // checking for tablet device
    if (utilityCommon.isTablet()) {
      this.showSnackBar(this._translateService.instant('$pdf_ios_preparing'));
      window.location.assign(`/api/pdf/assessment/${this.recommendation.relatedAssessmentId}`);
    } else {
      // Download pdf in Safari
      if (this.browser === 'Safari') {
        this.showSnackBar(this._translateService.instant('$pdf_android_download'));

        utilityCommon.pdfDownloadinSafari(this.recommendation.relatedAssessmentId, '');

      } else {
        switch (this.recommendation.relatedAssessmentType) {
          case this._translateService.instant(EntityTypeDynamic.SURVEY):
            if (this.isLinked) {
              this.dialog.open(SurveyReportDialogComponent, {
                height: '100%',
                width: '1000px',
                data: {
                  surveyId: this.recommendation.relatedAssessmentId,
                },
              });
              this._dialogRef.close();
            }
            break;
          case this._translateService.instant(EntityTypeDynamic.EVALUATION):
            if (this.isLinked) {
              this.dialog.open(ErgonomicsEvaluationReportDialogComponent, {
                height: '100%',
                width: '1000px',
                data: {
                  evaluationId: this.recommendation.relatedAssessmentId,
                },
              });
              this._dialogRef.close();
            }
            break;
        }
      }
    }
  }

  // User with edit can only edit the priority once the recommendation has been added to the user
  public updateRecommendation(): void {
    const updateRecommendation: RecommendationInterface = {
      ...this.recommendation,
      priority: this.recommendationFormGroup.value.priorityCtrl,
    };

    this._recommendationService.update(updateRecommendation);
  }

  // When user closes - only update the closed value and ignore any other potential changes
  public markRecommendationComplete(): void {
    const updateRecommendation: RecommendationInterface = {
      ...this.recommendation,
      isComplete: true,
    };

    this._recommendationService.update(updateRecommendation).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this._recommendationService.setRecommendationValue(true);
    });
  }


  // Delete recommendation
  public deleteRecommendation(): void {
    this._recommendationService.deleteRecommendationById(this.recommendation);
    this._dialogRef.close();
  }
  private showSnackBar(message: string): void {
    this._snackBar.open(message, undefined, {
      duration: 5000,
    });
  }

  public getBackupValue(field: string, object: any, isConvertion: any) {
    return getBackupLanguageValue(field, object, this._GlobalConstants.cultureCookieId, isConvertion, this._GlobalConstants.backupLanguageId);
  }

}
